import React, {
    Key,
    MutableRefObject,
    RefObject,
    useContext,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from 'react'
import Pagination from './Pagination'
import {ExclamationIcon, SearchIcon} from '@heroicons/react/outline'
import {bind, classNames, onEnter, switcher} from '../wrapper'
import {arrayUpdatePartial} from "../immutableState";
import PaginationResponse from "../generated/interfaces/paginationResponse";
import PaginationRequestSearch from "../generated/interfaces/paginationRequestSearch";
import AppContext from "../pages/appContext";
import Success from "./Success";
import CheckBox from "./CheckBox";

export function useMountedPromise() {
    const mounted = useRef(false);

    function createPromise<T>(promise: Promise<T>): Promise<T> {
        return new Promise<T>((res, rej) => {
            promise.then(resp => {
                if (mounted.current)
                    res(resp);
            }).catch(err => {
                rej(err);
            })
        });
    }
    useEffect(() => {
        mounted.current = true;

        return () => {
            mounted.current = false;
        }
    }, []);

    return {
        createPromise,
        mounted
    }
}
export interface PagedTableFunctions<T> {
    refresh: () => void;
    updateData: (update: (prev: T[]) => T[]) => void;
    updateRow: (predicate: ((row: T) => boolean), setRow: (row: T) => void) => void;
    focus: () => void;
    getData: () => T[];
    clearSelection: () => void;
}

// Row-selection support (mirrors the itest-leaf PagedSearchTable API): when supplied, the table
// renders a leading checkbox column with a header select-all over the visible rows. Selection keys
// come from keyExtractor, and the parent owns the selectedKeys state.
export interface SelectionProps {
    selectedKeys: Key[];
    onSelectionChange: (keys: Key[]) => void;
    // Show the header select-all checkbox (default true).
    selectAll?: boolean;
}

export interface TableColumn<TResponse> {
    header: React.ReactNode | (() => React.ReactNode);
    row: (item: TResponse, index: number) => React.ReactNode;
    key?: Key;

    // return colspan 0 if column should not be rendered.
    colspan?: (item: TResponse) => number;
    classNames?: (item: TResponse) => string;
}
interface PagedTableProps<TResponse, TRequest extends PaginationRequestSearch> {
    call: ((request: TRequest) => Promise<PaginationResponse<TResponse>>)
        | ((request: TRequest) => PaginationResponse<TResponse>)
    buildSearch?: (base: PaginationRequestSearch) => TRequest;
    topSlot?: React.ReactNode;
    summary?: (rows: TResponse[]) => React.ReactNode;

    componentRef?: MutableRefObject<PagedTableFunctions<TResponse> | undefined>;
    downloadExcelCall?: ((resp: PaginationRequestSearch) => void);

    inputSlot?: React.ReactNode;
    searchSlot?: React.ReactNode;

    keyExtractor?: (item: TResponse) => Key;
    columns: TableColumn<TResponse>[],
    rowClick?: (item: TResponse) => void;

    onChange?: (rows: TResponse[], paging: PagingData) => void;
    selection?: SelectionProps;
    // When selection is enabled, rows for which this returns false cannot be checked (their checkbox
    // renders disabled) and are skipped by the header select-all.
    isSelectable?: (item: TResponse) => boolean;
    // Tooltip shown on hover over a disabled row's checkbox, explaining why it can't be selected.
    unselectableTooltip?: string | ((item: TResponse) => string);
}

export interface PagingData {
    page: number;
    rowsPerPage: number;
    search: string;
    all: boolean
}

enum TableStatus { loaded, loading, error}

const PagedSearchTable = <TResponse extends object, TRequest extends PaginationRequestSearch = PaginationRequestSearch>(
    {
        call,
        summary,
        keyExtractor,
        columns,
        rowClick,
        buildSearch,
        topSlot,
        componentRef,
        downloadExcelCall,
        inputSlot,
        onChange,
        searchSlot,
        selection,
        isSelectable,
        unselectableTooltip
    }: PagedTableProps<TResponse, TRequest>) => {
    const [data, setData] = useState<PaginationResponse<TResponse>>({
        items: [],
        total: 0
    })
    const context = useContext(AppContext)

    const [selectedRow, setSelectedRow] = useState<number | null>(null)

    const [paging, setPaging] = useState<PagingData>({
        page: 0,
        rowsPerPage: 20,
        search: '',
        all: false
    })

    const [search, setSearch] = useState('')
    const [tableState, setTableState] = useState<TableStatus>(TableStatus.loading)
    const [focusVal, setFocusVal] = useState(0)
    const {createPromise} = useMountedPromise();

    function pagingDate(page: number | null, rowsPerPage: number | null, search: string | null, all: boolean | null): PagingData {
        return {
            search: search ?? paging.search,
            rowsPerPage: rowsPerPage ?? paging.rowsPerPage,
            page: page ?? paging.page,
            all: all ?? paging.all
        } as PagingData
    }

    function updateData(resp: PaginationResponse<TResponse>, set: PagingData) {
        setData(resp);
        setTableState(TableStatus.loaded);
        onChange?.(resp.items, set);
    }

    function updatePage(set: PagingData) {
        setTableState(TableStatus.loading)
        setPaging(set)
        const request: PaginationRequestSearch = {
            sortBy: '',
            search: set.search,
            rowsPerPage: set.rowsPerPage,
            page: set.page,
            ascending: false,
            all: false
        }
        // provide for custom search request object that extends PaginationRequestSearch
        const result = call(buildSearch ? buildSearch(request) : request as TRequest)
        if (result instanceof Promise) {
            createPromise<PaginationResponse<TResponse>>(result).then(resp => {
                // resp is AxiosResponse<PaginationResponse> | PaginationResponse
                updateData(resp, set);
            }).catch(() => {
                setTableState(TableStatus.error)
            })
        } else {
            updateData(result, set);
        }
    }

    function doSearch() {
        updatePage(pagingDate(0, null, search, null))
    }

    useImperativeHandle(componentRef, () => ({
        refresh: doSearch,
        getData: () => data.items,
        updateData: (update: (prev: TResponse[]) => TResponse[]) => {
            const newItems = update(data.items)
            setData({...data, items: newItems})
        },
        updateRow: (predicate: ((row: TResponse) => boolean), setRow: (row: TResponse) => void) => {
            const row = data.items.find(predicate)
            if (row) {
                setRow(row)
                setData({...data, items: data.items})
            }
        },
        focus,
        clearSelection: () => selection?.onSelectionChange([])
    }))

    const input = useRef<HTMLInputElement>(null)
    const app = useContext(AppContext)

    // Rerun the useEffect to focus on input
    function focus() {
        setFocusVal(Math.random())
    }

    useEffect(() => {
        // fire and focus on focus function.
        input.current?.focus()
    }, [focusVal])

    useEffect(() => {
        // useEffect is fired after initial render and setRef
        updatePage(paging)
    }, [])

    function handleRowClick(row: TResponse, index: number) {
        rowClick?.(row)
        setSelectedRow(index)
    }

    function rowKeyOf(row: TResponse, index: number): Key {
        return keyExtractor?.(row) ?? (paging.page * paging.rowsPerPage) + index
    }

    function isRowSelectable(row: TResponse): boolean {
        return !isSelectable || isSelectable(row)
    }

    // Keys of the visible rows that are actually selectable - the header select-all and the
    // "all selected" state ignore rows that can't be checked.
    const selectableKeys = () => data.items
        .map((row, index) => ({row, key: rowKeyOf(row, index)}))
        .filter(({row}) => isRowSelectable(row))
        .map(({key}) => key)

    const allVisibleSelected = selection != null && selectableKeys().length > 0
        && selectableKeys().every(k => selection.selectedKeys.includes(k))

    function toggleRowSelection(key: Key) {
        if (!selection) return
        selection.onSelectionChange(
            selection.selectedKeys.includes(key)
                ? selection.selectedKeys.filter(k => k !== key)
                : [...selection.selectedKeys, key]
        )
    }

    // Select-all toggles only the currently visible (selectable) rows, preserving keys selected on
    // other pages.
    function toggleSelectAll() {
        if (!selection) return
        const keys = selectableKeys()
        if (allVisibleSelected) {
            selection.onSelectionChange(selection.selectedKeys.filter(k => !keys.includes(k)))
        } else {
            const next = [...selection.selectedKeys]
            keys.forEach(k => { if (!next.includes(k)) next.push(k) })
            selection.onSelectionChange(next)
        }
    }

    function returnPaging() {
        const req: PaginationRequestSearch = {
            sortBy: '',
            search: search,
            rowsPerPage: paging.rowsPerPage,
            page: paging.page,
            ascending: false,
            all: paging.all
        }

        return buildSearch ? buildSearch(req) : req
    }

    return (
        <div className="w-full">

            <div className="rounded-md border my-1 bg-white relative">
                {topSlot}
                {
                    switcher(tableState, c => c
                        .case(s => s === TableStatus.loaded || s === TableStatus.loading, () =>
                            <>
                                {tableState === TableStatus.loading
                                    ? <div
                                        className="absolute inset-0 bg-overlay-200 flex justify-center items-center">
                                        <img src='/images/loader.gif' alt=''/>
                                    </div>
                                    : null}
                                <div className="flex w-full bg-white py-1 items-center">
                                    <div className="px-2 text-gray-400">
                                        <SearchIcon height={24}/>
                                    </div>
                                    <div className="w-full">
                                        <input ref={input} type="text" className="w-full outline-none"
                                               onKeyUp={onEnter(doSearch)} {...bind(search, setSearch)}/>
                                        <div className={"flex justify-end"}>
                                            {inputSlot}
                                        </div>
                                    </div>
                                    {
                                        downloadExcelCall
                                            ? <div className='px-2 p-1 cursor-pointer hover:bg-gray-100 ' onClick={() => {
                                                downloadExcelCall(returnPaging())
                                                context.showSnack(<Success title={"Starting download"}/>)
                                            }}>
                                                <img className='w-[30px]' src='/images/csv.png' alt=""/>
                                            </div>
                                            : null
                                    }
                                    {searchSlot}
                                    <div className="btn bg-primary ml-1" onClick={doSearch}>search</div>
                                </div>

                                <div className="w-full overflow-x-scroll">
                                    <table className="min-w-full divide-y divide-gray-300 w-full">
                                        <thead className="bg-gray-50">
                                        <tr>
                                            {selection ? (
                                                <th scope="col" className="px-2 py-2 w-10 text-center">
                                                    {selection.selectAll !== false
                                                        ? <CheckBox checked={allVisibleSelected} onChange={toggleSelectAll}/>
                                                        : null}
                                                </th>
                                            ) : null}
                                            {columns.map((c, i) => (
                                                <th scope="col"
                                                    className="uppercase px-2 py-2 text-gray-500 text-left text-sm font-semibold "
                                                    key={i}>{typeof c.header === 'function' ? c.header() : c.header}</th>))}
                                        </tr>
                                        </thead>
                                        <tbody className="bg-white">

                                        {data.total == 0
                                            ?(
                                                <tr>
                                                   <td colSpan={columns.length + (selection ? 1 : 0)}>
                                                        <div className='p-4'>
                                                            <div className='border w-full p-2 border-gray-200 border-dashed text-gray-500'>
                                                                No Results
                                                            </div>
                                                        </div>
                                                   </td>
                                                </tr>
                                            )
                                        : data.items.map((row, index) => (
                                                    <tr key={rowKeyOf(row, index)}
                                                        className={classNames(selectedRow == index ? 'bg-[#314516]' : index % 2 === 0 ? 'hover:bg-gray-200' : 'bg-gray-50 hover:bg-gray-200', 'group cursor-pointer')}
                                                        onClick={() => handleRowClick(row, index)}>

                                                        {selection ? (
                                                            <td className="px-2 py-2 w-10 text-center" onClick={e => e.stopPropagation()}>
                                                                {isRowSelectable(row)
                                                                    ? <CheckBox checked={selection.selectedKeys.includes(rowKeyOf(row, index))}
                                                                                onChange={() => toggleRowSelection(rowKeyOf(row, index))}/>
                                                                    : <div className="opacity-40 cursor-not-allowed inline-block"
                                                                           title={typeof unselectableTooltip === 'function' ? unselectableTooltip(row) : unselectableTooltip}>
                                                                        <div className="pointer-events-none"><CheckBox checked={false}/></div>
                                                                    </div>}
                                                            </td>
                                                        ) : null}

                                                        {columns.map((c, i) => {
                                                                const colspan = c.colspan?.(row) ?? 1;
                                                                if (colspan == 0)
                                                                    return null;

                                                                return (
                                                                    <td colSpan={colspan}
                                                                        className={classNames('whitespace-nowrap px-1 py-2 text-sm', c.classNames?.(row), selectedRow == index ? 'text-white' : 'text-gray-500')}
                                                                        key={i}>{c.row(row, index)}</td>)
                                                            }
                                                        )}
                                                    </tr>
                                                ))
                                        }
                                        </tbody>
                                    </table>

                                </div>
                                <Pagination refresh={() => componentRef?.current?.refresh()}
                                            total={data.total}
                                            rowsPerPage={paging.rowsPerPage}
                                            page={paging.page}

                                            gotoPage={(page) => updatePage(pagingDate(page, null, null, null))}
                                            setRowsPerPage={rows => updatePage(pagingDate(0, rows, null, null))}/>
                                {summary?.(data.items)}
                            </>
                        )
                        .case(TableStatus.error, () => <div className="flex w-full m-4 justify-center items-center">
                            <div className="px-2"><ExclamationIcon className="w-10 h-10 text-red-400"/></div>
                            <div className="text-gray-600">Error loading table data</div>
                        </div>)
                    )
                }

            </div>
        </div>
    )
}

export default PagedSearchTable
